import { roundToDecimals } from '@iconik-io/core/src/round_to_decimals';

import type { Point } from '../types.js';

// Width and height 1 to make the calculations between 0 and 1
const width = 1;
const height = 1;
// Function to calculate angle to x-axis
function angleToXAxis(p1: Point, p2: Point): number {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}
// Function to check if a point is within the board
function isWithinBoard(x: number, y: number): boolean {
  return x >= 0 && x <= width && y >= 0 && y <= height;
}
// Calculate intersection points between a line and a container, based on starting coordinates and angle
function calculateIntersectionPoints(
  startX: number,
  startY: number,
  angle: number,
): Point[] {
  // Convert angle to radians
  const angleRadians = (angle * Math.PI) / 180;

  // Calculate slope of the line
  const slope = Math.tan(angleRadians);

  // Calculate y-intercept
  const yIntercept = startY - slope * startX;

  // Initialize array to store valid intersection points
  const intersectionPoints = [];

  // Calculate intersectionPoints
  const xTop = (height - yIntercept) / slope;
  const yRight = slope * width + yIntercept;
  const xBottom = -yIntercept / slope;
  const yLeft = yIntercept;
  // Calculate points of intersection
  if (isWithinBoard(xTop, height)) {
    intersectionPoints.push({ x: xTop, y: height });
  }

  if (isWithinBoard(xBottom, 0)) {
    intersectionPoints.push({ x: xBottom, y: 0 });
  }

  if (isWithinBoard(width, yRight)) {
    intersectionPoints.push({ x: width, y: yRight });
  }

  if (isWithinBoard(0, yLeft)) {
    intersectionPoints.push({ x: 0, y: yLeft });
  }

  return intersectionPoints;
}

// Get the corners/vertices of the polygon
function calculatePolygonCorners(
  startX: number,
  startY: number,
  angle: number,
) {
  const corners = {
    bottomLeft: { x: 0, y: 0 },
    bottomRight: { x: width, y: 0 },
    topRight: { x: width, y: height },
    topLeft: { x: 0, y: height },
  };

  const intersectionPoints = calculateIntersectionPoints(startX, startY, angle);

  const allCorners = [...intersectionPoints];
  // Add relevant corners based on the angle
  if (angle < 0) {
    allCorners.push(corners.bottomLeft);
    if (intersectionPoints.some((intersection) => intersection.x === width)) {
      allCorners.push(corners.bottomRight);
    }
    if (intersectionPoints.some((intersection) => intersection.y === height)) {
      allCorners.push(corners.topLeft);
    }
  } else if (angle >= 0 && angle <= 90) {
    allCorners.push(corners.bottomRight);
    if (intersectionPoints.some((intersection) => intersection.x === 0)) {
      allCorners.push(corners.bottomLeft);
    }
    if (intersectionPoints.some((intersection) => intersection.y === height)) {
      allCorners.push(corners.topRight);
    }
  } else if (angle > 90 && angle <= 180) {
    allCorners.push(corners.topRight);
    if (intersectionPoints.some((intersection) => intersection.x === 0)) {
      allCorners.push(corners.topLeft);
    }
    if (intersectionPoints.some((intersection) => intersection.y === 0)) {
      allCorners.push(corners.bottomRight);
    }
  } else if (angle > 180 && angle <= 270) {
    allCorners.push(corners.topLeft);
    if (intersectionPoints.some((intersection) => intersection.x === width)) {
      allCorners.push(corners.topRight);
    }
    if (intersectionPoints.some((intersection) => intersection.y === 0)) {
      allCorners.push(corners.bottomLeft);
    }
  } else if (angle > 270) {
    allCorners.push(corners.bottomLeft);
    if (intersectionPoints.some((intersection) => intersection.x === width)) {
      allCorners.push(corners.bottomRight);
    }
    if (intersectionPoints.some((intersection) => intersection.y === height)) {
      allCorners.push(corners.topLeft);
    }
  }
  // Find the bottom-left point
  allCorners.sort((a, b) => {
    if (a.y === b.y) {
      return a.x - b.x;
    }
    return a.y - b.y;
  });
  const start = allCorners.shift();
  if (start) {
    // Sort points clockwise
    allCorners.sort((a, b) => {
      const angleA = angleToXAxis(start, a);
      const angleB = angleToXAxis(start, b);
      return angleA - angleB;
    });

    // Add the starting point back to the beginning of the array
    allCorners.unshift(start);
  }
  return allCorners;
}

/**
 * Returns a polygon for setting a clip path based on a wipe point and an angle inside a rectangle
 * @param startX - X value between 0-1 for the starting point
 * @param startY - Y value between 0-1 for the starting point
 * @param angle - A normalized angle in [0..1] range where:
 *                0.0 = 0° (East), 0.25 = 90° (North), 0.5 = 180° (West), 0.75 = 270° (South)
 *
 * @returns A string of `polygon({clipPath})` to be used for setting `style.clipPath`
 * @example
 * ```
 *  const clipPath = getClipPathPolygon(wipe.x, wipe.y, wipe.angle);
 *  this.mediaElement.style.clipPath = `${clipPath}`;
 * ```
 */

export function getClipPathPolygon(
  startX: number,
  startY: number,
  angle: number,
): string {
  // Convert normalized angle [0..1] to degrees [0..360]
  // User angle is counter-clockwise from East: 0 = East (0°), 0.25 = North (90°), 0.5 = West (180°), 0.75 = South (270°)
  // Screen coordinates have y increasing downward, so standard trig is clockwise
  // To convert counter-clockwise user angle to clockwise screen angle: negate it
  let angleDegrees = -(angle * 360);

  // Normalize to [0, 360) range. Use modulo to correctly handle angles outside
  // the single-wrap range (e.g. >360 or <-360). JS % can be negative, so use
  // a double-mod trick to ensure a positive result.
  angleDegrees = ((angleDegrees % 360) + 360) % 360;

  // Note: the `angle` parameter may be any numeric value (not necessarily
  // pre-normalized). This function will normalise it into [0,360) before
  // computing the clip polygon.

  const polygonCorners = calculatePolygonCorners(startX, startY, angleDegrees);

  const clipPath = polygonCorners
    .map((point) => {
      return `${roundToDecimals(point.x, 4) * 100}% ${roundToDecimals(point.y, 4) * 100}%`;
    })
    .join(', ');
  return `polygon(${clipPath})`;
}
